import React, { useContext, useEffect, useRef, useState } from 'react';
import { MdSearch, MdEdit, MdFilterList, MdDownload, MdSave, MdCancel, MdRefresh } from 'react-icons/md';
import { Dropdown, Breadcrumb, ButtonGroup, Button, Form, Modal, OverlayTrigger, Tooltip } from 'react-bootstrap';
import * as XLSX from 'xlsx';
import './UAEFDLDataManagement.scss';
import { withTranslation } from 'react-i18next';
import CompanyService from '../../../../core/service/CompanyService';
import GHGEmissionService from '../../../../core/service/GHGEmissionService';
import SubStdService from '../../../../core/service/SubStdService';
import { LS_AUTHORITY_KEY, LS_GHG_DV_FILTER_KEY, Months, startYear } from '../../../../core/constants/common';
import { RegulationContext, LoaderContext } from '../../../../App';
import Utils from '../../../../core/utilities/Utils';
import { toast } from 'react-toastify';
import useHasPermission from '../../../../core/hooks/useHasPermission';

const UAEFDLDataManagementHeader = ({ t, onFilterChange, onEditModeChange, onSaveData, isEditMode, hasChanges, hasValidationErrors, onSearchChange, onViewChange, editableData, tableRows, branchNames, tableEdits, tableData, onClearEditableData }) => {
  const { hasGHGEmissionsUpdatePermission } = useHasPermission();
  const ghgdvFilter = JSON.parse(localStorage.getItem(LS_GHG_DV_FILTER_KEY));
  const { glbRegulation } = useContext(RegulationContext);
  const { setLoaderDisplay } = useContext(LoaderContext);
  const glbRegulationId = glbRegulation?.id ?? "";
  const [activeView, setActiveView] = useState('combined');

  // Function to get all entity IDs (both parent and child)
  const getAllEntityIds = () => {
    const allIds = [];
    parentEntities.forEach(entity => {
      allIds.push(entity.id);
    });
    childEntities.forEach(entity => {
      allIds.push(entity.id);
    });
    return allIds;
  };
  const month = Utils.getMonthFromFrequency(glbRegulation?.frequency);
  const year = new Date().getFullYear();
  const lsData = JSON.parse(localStorage.getItem(LS_AUTHORITY_KEY));
  const companyId = lsData.profile.company.id;
  const [parentEntities, setParentEntities] = useState([]);
  const [childEntities, setChildEntities] = useState([]);
  const [scopeCategoryData, setScopeCategoryData] = useState([]);
  const [filter, setFilter] = useState({
    periodFrom: '2025-01-01',
    periodTo: '2025-12-31',
    scope: [],
    category: [],
    businessUnitIds: [],
    period: '2025-01-01'
  });
  const [frequencymonth, setFrequencyMonth] = useState(String(new Date().getMonth() + 1).padStart(2, '0'));
  const [annualyear, setAnnualYear] = useState(new Date().getFullYear());

  // New state variables for from/to period dropdowns
  const [fromPeriodYear, setFromPeriodYear] = useState(new Date().getFullYear());
  const [fromPeriodMonth, setFromPeriodMonth] = useState(String(new Date().getMonth() + 1).padStart(2, '0'));
  const [toPeriodYear, setToPeriodYear] = useState(new Date().getFullYear());
  const [toPeriodMonth, setToPeriodMonth] = useState(String(new Date().getMonth() + 1).padStart(2, '0'));
  const [showFromPeriodDropdown, setShowFromPeriodDropdown] = useState(false);
  const [showToPeriodDropdown, setShowToPeriodDropdown] = useState(false);
  const [fromPeriodError, setFromPeriodError] = useState('');
  const [toPeriodError, setToPeriodError] = useState('');

  const optionsMonth = Months({ t });
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;
  const [yearOptions, setYearOptions] = useState([]);
  const [isSearchVisible, setIsSearchVisible] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showPeriodDropdown, setShowPeriodDropdown] = useState(false);
  const [showScopeDropdown, setShowScopeDropdown] = useState(false);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [showParentEntityDropdown, setShowParentEntityDropdown] = useState(false);
  const [showChildEntityDropdown, setShowChildEntityDropdown] = useState(false);
  const [showCancelConfirmation, setShowCancelConfirmation] = useState(false);
  const [showPeriodFromDropdown, setShowPeriodFromDropdown] = useState(false);
  const [showPeriodToDropdown, setShowPeriodToDropdown] = useState(false);
  const [periodFromError, setPeriodFromError] = useState('');
  const [periodToError, setPeriodToError] = useState('');

  // Advanced Period Selection Modal State
  const [showAdvancedPeriodModal, setShowAdvancedPeriodModal] = useState(false);
  const [periodType, setPeriodType] = useState('yearly');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedQuarter, setSelectedQuarter] = useState('Q1');
  const [selectedMonth, setSelectedMonth] = useState('01');
  const [customFromYear, setCustomFromYear] = useState(new Date().getFullYear());
  const [customFromMonth, setCustomFromMonth] = useState('01');
  const [customToYear, setCustomToYear] = useState(new Date().getFullYear());
  const [customToMonth, setCustomToMonth] = useState('12');
  const [customPeriodError, setCustomPeriodError] = useState('');



  // Function to get month name from month number
  const getMonthName = (monthNumber) => {
    const months = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    return months[parseInt(monthNumber) - 1] || '';
  };

  // Function to get formatted period display
  const getFormattedPeriod = () => {
    const monthName = getMonthName(frequencymonth);
    return `${monthName}, ${annualyear}`;
  };

  // New functions for from/to period handling
  const getFormattedFromPeriod = () => {
    const monthName = getMonthName(fromPeriodMonth);
    return `${monthName}, ${fromPeriodYear}`;
  };

  const getFormattedToPeriod = () => {
    const monthName = getMonthName(toPeriodMonth);
    return `${monthName}, ${toPeriodYear}`;
  };

  // Validate from/to period range
  const validateFromToPeriodRange = () => {
    const fromDate = new Date(`${fromPeriodYear}-${fromPeriodMonth}-01`);
    const toDate = new Date(getLastDayOfMonth(toPeriodYear, toPeriodMonth));

    // Reset errors
    setFromPeriodError('');
    setToPeriodError('');

    let isValid = true;

    // Check if from date is valid
    if (isNaN(fromDate.getTime())) {
      setFromPeriodError('Invalid date format');
      isValid = false;
    }

    // Check if to date is valid
    if (isNaN(toDate.getTime())) {
      setToPeriodError('Invalid date format');
      isValid = false;
    }

    // Check if from date is before or equal to to date
    if (fromDate > toDate) {
      setFromPeriodError('From period must be before or equal to to period');
      setToPeriodError('To period must be after or equal to from period');
      isValid = false;
    }

    return isValid;
  };

  const handleFromPeriodYearChange = (year) => {
    setFromPeriodYear(year);

    // Validate before updating filter
    const newFromDate = `${year}-${fromPeriodMonth}-01`;
    const newToDate = getLastDayOfMonth(toPeriodYear, toPeriodMonth);

    const fromDate = new Date(newFromDate);
    const toDate = new Date(newToDate);

    if (fromDate > toDate) {
      setFromPeriodError('From period must be before or equal to to period');
      setToPeriodError('To period must be after or equal to from period');
      return; // Don't update filter if validation fails
    }

    // Clear errors if validation passes
    setFromPeriodError('');
    setToPeriodError('');

    const newFilter = { ...filter, periodFrom: newFromDate };
    setFilter(newFilter);

    // Notify parent component
    if (onFilterChange) {
      onFilterChange(newFilter);
    }
  };

  const handleFromPeriodMonthChange = (month) => {
    setFromPeriodMonth(month);

    // Validate before updating filter
    const newFromDate = `${fromPeriodYear}-${month}-01`;
    const newToDate = getLastDayOfMonth(toPeriodYear, toPeriodMonth);

    const fromDate = new Date(newFromDate);
    const toDate = new Date(newToDate);

    if (fromDate > toDate) {
      setFromPeriodError('From period must be before or equal to to period');
      setToPeriodError('To period must be after or equal to from period');
      return; // Don't update filter if validation fails
    }

    // Clear errors if validation passes
    setFromPeriodError('');
    setToPeriodError('');

    const newFilter = { ...filter, periodFrom: newFromDate };
    setFilter(newFilter);

    // Notify parent component
    if (onFilterChange) {
      onFilterChange(newFilter);
    }
  };

  const handleToPeriodYearChange = (year) => {
    setToPeriodYear(year);

    // Validate before updating filter
    const newFromDate = `${fromPeriodYear}-${fromPeriodMonth}-01`;
    const newToDate = getLastDayOfMonth(year, toPeriodMonth);

    const fromDate = new Date(newFromDate);
    const toDate = new Date(newToDate);

    if (fromDate > toDate) {
      setFromPeriodError('From period must be before or equal to to period');
      setToPeriodError('To period must be after or equal to from period');
      return; // Don't update filter if validation fails
    }

    // Clear errors if validation passes
    setFromPeriodError('');
    setToPeriodError('');

    const newFilter = { ...filter, periodTo: newToDate };
    setFilter(newFilter);

    // Notify parent component
    if (onFilterChange) {
      onFilterChange(newFilter);
    }
  };

  const handleToPeriodMonthChange = (month) => {
    setToPeriodMonth(month);

    // Validate before updating filter
    const newFromDate = `${fromPeriodYear}-${fromPeriodMonth}-01`;
    const newToDate = getLastDayOfMonth(toPeriodYear, month);

    const fromDate = new Date(newFromDate);
    const toDate = new Date(newToDate);

    if (fromDate > toDate) {
      setFromPeriodError('From period must be before or equal to to period');
      setToPeriodError('To period must be after or equal to from period');
      return; // Don't update filter if validation fails
    }

    // Clear errors if validation passes
    setFromPeriodError('');
    setToPeriodError('');

    const newFilter = { ...filter, periodTo: newToDate };
    setFilter(newFilter);

    // Notify parent component
    if (onFilterChange) {
      onFilterChange(newFilter);
    }
  };

  // Advanced Period Selection Functions

  const handleAdvancedPeriodModalOpen = () => {
    setShowAdvancedPeriodModal(true);
  };

  const handleAdvancedPeriodModalClose = () => {
    setShowAdvancedPeriodModal(false);
  };

  const handlePeriodTypeChange = (type) => {
    setPeriodType(type);
    // Clear any custom period errors when switching types
    setCustomPeriodError('');
  };

  const handleAdvancedYearChange = (year) => {
    setSelectedYear(year);
  };

  const handleQuarterChange = (quarter) => {
    setSelectedQuarter(quarter);
  };

  const handleAdvancedMonthChange = (month) => {
    setSelectedMonth(month);
  };

  const handleCustomFromYearChange = (year) => {
    setCustomFromYear(year);
    // Clear error when user makes changes
    setCustomPeriodError('');
  };

  const handleCustomFromMonthChange = (month) => {
    setCustomFromMonth(month);
    // Clear error when user makes changes
    setCustomPeriodError('');
  };

  const handleCustomToYearChange = (year) => {
    setCustomToYear(year);
    // Clear error when user makes changes
    setCustomPeriodError('');
  };

  const handleCustomToMonthChange = (month) => {
    setCustomToMonth(month);
    // Clear error when user makes changes
    setCustomPeriodError('');
  };

  // Validate custom period selection
  const validateCustomPeriod = () => {
    const fromDate = new Date(`${customFromYear}-${customFromMonth}-01`);
    const toDate = new Date(getLastDayOfMonth(customToYear, customToMonth));

    // Check if from date is after to date
    if (fromDate > toDate) {
      return {
        isValid: false,
        message: t("AdvancedPeriodModal.fromDateAfterToDate.label")
      };
    }

    return { isValid: true };
  };

  const getQuarterDates = (year, quarter) => {
    const quarterMap = {
      'Q1': { startMonth: '01', endMonth: '03' },
      'Q2': { startMonth: '04', endMonth: '06' },
      'Q3': { startMonth: '07', endMonth: '09' },
      'Q4': { startMonth: '10', endMonth: '12' }
    };

    const quarterData = quarterMap[quarter];
    const startDate = `${year}-${quarterData.startMonth}-01`;
    const endDate = getLastDayOfMonth(year, quarterData.endMonth);

    return { startDate, endDate };
  };

  const getMonthDates = (year, month) => {
    const startDate = `${year}-${month}-01`;
    const endDate = getLastDayOfMonth(year, month);
    return { startDate, endDate };
  };

  const getYearDates = (year) => {
    const startDate = `${year}-01-01`;
    const endDate = `${year}-12-31`;
    return { startDate, endDate };
  };

  const getCustomDates = (fromYear, fromMonth, toYear, toMonth) => {
    const startDate = `${fromYear}-${fromMonth}-01`;
    const endDate = getLastDayOfMonth(toYear, toMonth);
    return { startDate, endDate };
  };

  const handleApplyAdvancedPeriod = () => {
    let startDate, endDate;

    switch (periodType) {
      case 'yearly':
        const yearlyDates = getYearDates(selectedYear);
        startDate = yearlyDates.startDate;
        endDate = yearlyDates.endDate;
        break;
      case 'quarterly':
        const quarterlyDates = getQuarterDates(selectedYear, selectedQuarter);
        startDate = quarterlyDates.startDate;
        endDate = quarterlyDates.endDate;
        break;
      case 'monthly':
        const monthlyDates = getMonthDates(selectedYear, selectedMonth);
        startDate = monthlyDates.startDate;
        endDate = monthlyDates.endDate;
        break;
      case 'custom':
        const customDates = getCustomDates(customFromYear, customFromMonth, customToYear, customToMonth);
        startDate = customDates.startDate;
        endDate = customDates.endDate;
        break;
      default:
        return;
    }

    // Validate date range
    const fromDate = new Date(startDate);
    const toDate = new Date(endDate);

    // Check if dates are valid
    if (isNaN(fromDate.getTime()) || isNaN(toDate.getTime())) {
      toast.error(t("AdvancedPeriodModal.invalidDateRange.label"), {
        position: toast.POSITION.TOP_RIGHT,
      });
      return;
    }

    // Check if from date is after to date
    if (fromDate > toDate) {
      const errorMessage = t("AdvancedPeriodModal.fromDateAfterToDate.label");
      if (periodType === 'custom') {
        setCustomPeriodError(errorMessage);
      } else {
        toast.error(errorMessage, {
          position: toast.POSITION.TOP_RIGHT,
        });
      }
      return;
    }

    // Update the filter with new period
    const updatedFilter = {
      ...filter,
      periodFrom: startDate,
      periodTo: endDate,
      period: startDate
    };

    setFilter(updatedFilter);

    // Update from/to period state variables
    const fromDateParts = startDate.split('-');
    const toDateParts = endDate.split('-');

    setFromPeriodYear(parseInt(fromDateParts[0]));
    setFromPeriodMonth(fromDateParts[1]);
    setToPeriodYear(parseInt(toDateParts[0]));
    setToPeriodMonth(toDateParts[1]);

    // Notify parent component about the filter change
    if (onFilterChange) {
      onFilterChange(updatedFilter);
    }

    // Close the modal
    setShowAdvancedPeriodModal(false);

    toast.success(t("AdvancedPeriodModal.periodFilterApplied.label"), {
      position: toast.POSITION.TOP_RIGHT,
    });
  };

  // Generate year options for dropdowns
  const generateYearOptions = () => {
    const years = [];
    for (let i = currentYear; i >= startYear; i--) {
      years.push(
        <option value={i} key={i}>{i}</option>
      );
    }
    return years;
  };

  // Generate quarter options
  const generateQuarterOptions = () => {
    return [
      <option value="Q1" key="Q1">{t("AdvancedPeriodModal.quarter1.label")}</option>,
      <option value="Q2" key="Q2">{t("AdvancedPeriodModal.quarter2.label")}</option>,
      <option value="Q3" key="Q3">{t("AdvancedPeriodModal.quarter3.label")}</option>,
      <option value="Q4" key="Q4">{t("AdvancedPeriodModal.quarter4.label")}</option>
    ];
  };

  // Generate month options
  const generateMonthOptions = () => {
    return optionsMonth?.map((item) => (
      <option value={item.value} key={item.value}>
        {item.label}
      </option>
    ));
  };

  // Save functionality
  const handleCancelEdit = () => {
    if (hasChanges) {
      setShowCancelConfirmation(true);
    } else {
      // No changes, just exit edit mode
      onEditModeChange && onEditModeChange(false);
    }
  };

  const handleCancelConfirm = () => {
    // User confirmed cancel - refresh table with original data and exit edit mode
    setShowCancelConfirmation(false);

    // Clear all editable data first
    if (onClearEditableData) {
      onClearEditableData();
    }

    // Exit edit mode
    onEditModeChange && onEditModeChange(false);

    // Force a complete refresh by creating a new filter object
    if (onFilterChange) {
      const newFilter = { ...filter };
      onFilterChange(newFilter);
    }

    toast.info("Changes discarded, table refreshed with original data", {
      position: toast.POSITION.TOP_RIGHT,
    });
  };

  const handleCancelCancel = () => {
    // User cancelled the cancel - stay in edit mode
    setShowCancelConfirmation(false);
  };

  const handleRefresh = () => {
    // Clear all editable data first
    if (onClearEditableData) {
      onClearEditableData();
    }

    // Exit edit mode if in edit mode
    if (isEditMode) {
      onEditModeChange && onEditModeChange(false);
    }

    // Force a complete refresh by creating a new filter object
    if (onFilterChange) {
      const newFilter = { ...filter };
      onFilterChange(newFilter);
    }

    toast.info("Table data refreshed", {
      position: toast.POSITION.TOP_RIGHT,
    });
  };

  const handleDownloadExcel = () => {
    const rows = tableData?.tableRows ?? tableRows ?? [];
    const branches = tableData?.branchNames ?? branchNames ?? [];
    if (!rows.length) {
      toast.warning(t("GHGEmissionDataManagement.noDataToExport.label") || "No data available to export", {
        position: toast.POSITION.TOP_RIGHT,
      });
      return;
    }
    const scopeLabel = t("GHGEmissionDataManagement.excel.scope.label") || "Scope";
    const categoryLabel = t("GHGEmissionDataManagement.excel.category.label") || "Category";
    const emissionSourcesLabel = t("GHGEmissionDataManagement.excel.emissionSources.label") || "Emission Sources";
    const emissionTco2eLabel = t("GHGEmissionDataManagement.excel.emissionInTCO2e.label") || "Emission in tCO2e";
    const unitLabel = t("GHGEmissionDataManagement.excel.unit.label") || "Unit";
    const attachmentsLabel = t("GHGEmissionDataManagement.excel.attachments.label") || "Attachments";
    const commentsLabel = t("GHGEmissionDataManagement.excel.comments.label") || "Comments";
    const emissionFactorsLabel = t("GHGEmissionDataManagement.excel.emissionFactors.label") || "Emission factors";
    const headers = [scopeLabel, categoryLabel, emissionSourcesLabel, emissionTco2eLabel, unitLabel, ...branches, attachmentsLabel, commentsLabel, emissionFactorsLabel];
    const excelData = rows.map(row => {
      const branchValues = branches.map(branchName => {
        const cell = row[branchName];
        return cell && (cell.value !== null && cell.value !== undefined) ? cell.value : '';
      });
      return [
        row.scope ?? '',
        row.category ?? '',
        row.metricName ?? '',
        row.emissionInTCO2e ?? '',
        row.unit ?? '',
        ...branchValues,
        row.attachments ?? '',
        row.comments ?? '',
        row.emissionFactors !== null && row.emissionFactors !== undefined ? row.emissionFactors : ''
      ];
    });
    const worksheet = XLSX.utils.aoa_to_sheet([headers, ...excelData]);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, t("GHGEmissionDataManagement.excel.sheetName.label") || "UAE-FDL Data");
    const fileName = `GHG_Emission_Data_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(workbook, fileName);
    toast.success(t("GHGEmissionDataManagement.excel.downloadSuccess.label") || "Excel file downloaded successfully", {
      position: toast.POSITION.TOP_RIGHT,
    });
  };

  const handleEditModeEnter = () => {
    // Set current month and year when entering edit mode
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = String(currentDate.getMonth() + 1).padStart(2, '0');

    // Update the state variables
    setAnnualYear(currentYear);
    setFrequencyMonth(currentMonth);

    // Update period to first and last day of selected month when entering edit mode
    const periodFrom = `${currentYear}-${currentMonth}-01`;
    const periodTo = getLastDayOfMonth(currentYear, currentMonth);

    // Update the filter with the correct period range
    const updatedFilter = {
      ...filter,
      periodFrom: periodFrom,
      periodTo: periodTo
    };

    setFilter(updatedFilter);

    // Notify parent component about the filter change
    if (onFilterChange) {
      onFilterChange(updatedFilter);
    }

    // Enter edit mode
    onEditModeChange && onEditModeChange(true);
  };

  const handleSaveData = async () => {
    // Check for validation errors first
    if (hasValidationErrors) {
      toast.error("Please fix validation errors before saving. Values require UOM selection.", {
        position: toast.POSITION.TOP_RIGHT,
      });
      return;
    }

    // Use tableEdits as the primary source of changes
    let changes = tableEdits || {};

    if (!changes || Object.keys(changes).length === 0) {
      toast.warning("No changes to save", {
        position: toast.POSITION.TOP_RIGHT,
      });
      return;
    }

    // Check if there are actual changes (not just empty values)
    const hasActualChanges = Object.keys(changes).some(rowKey => {
      const rowData = changes[rowKey];
      const hasRowChanges = Object.keys(rowData).some(columnKey => {
        const value = rowData[columnKey];
        // Allow any non-null, non-undefined value as a valid change
        // This includes empty strings (user might want to clear a value), numbers, and strings
        // Also check for UOM changes (columns ending with _uom)
        const isValidValue = value !== undefined && value !== null;
        const isUOMChange = columnKey.endsWith('_uom') && value !== undefined && value !== null;
        return isValidValue || isUOMChange;
      });
      return hasRowChanges;
    });

    if (!hasActualChanges) {
      toast.warning("No valid changes to save", {
        position: toast.POSITION.TOP_RIGHT,
      });
      return;
    }

    // Transform editable data to the format expected by the API
    const dataPointsToSave = [];

    Object.keys(changes).forEach(rowKey => {
      // Find the row by matching the full rowKey pattern
      const rowData = changes[rowKey];

      // Find the original row by matching the rowKey pattern
      const originalRow = tableRows.find(row => {
        const expectedRowKey = `${row.scope}-${row.category}-${row.metricName}`;
        return expectedRowKey === rowKey;
      });

      if (originalRow) {
        // For each branch column that has been edited
        branchNames.forEach(branchName => {
          const hasValueChange = rowData[branchName] !== undefined;
          const hasUOMChange = rowData[`${branchName}_uom`] !== undefined;

          // Save if either the value or UOM has been changed
          if (hasValueChange || hasUOMChange) {
            // Find the branch data from the original row (row[branchName] has id, value, name)
            const branchData = originalRow[branchName];
            // Attachments live on the branch in originalMetric, not row.attachments (which is a count)
            const fullBranch = originalRow.originalMetric?.branches?.find(b => b.name === branchName);
            const attachmentsArray = Array.isArray(fullBranch?.attachments) ? fullBranch.attachments : [];

            // Create data point object for the API (attachments must be an array)
            const dataPoint = {
              branchId: branchData?.id || branchName, // Use branch ID if available, otherwise use name
              dataPointId: originalRow.dataPointId || originalRow.id, // Use dataPointId if available, otherwise use id
              value: hasValueChange ? Utils.parseLocaleNumber(String(rowData[branchName])) : (branchData?.value || 0),
              uom: rowData[`${branchName}_uom`] || originalRow.unit || '',
              remark: originalRow.comments || '',
              attachments: attachmentsArray
            };

            dataPointsToSave.push(dataPoint);
          }
        });
      }
    });

    if (dataPointsToSave.length === 0) {
      toast.warning("No valid changes to save", {
        position: toast.POSITION.TOP_RIGHT,
      });
      return;
    }

    setLoaderDisplay(true);

    try {
      const response = await SubStdService.saveBranchDataPoint(
        glbRegulationId,
        filter.periodFrom,
        { dataPoints: dataPointsToSave }
      );

      setLoaderDisplay(false);

      if (response.status === 200) {
        toast.success("Data saved successfully", {
          position: toast.POSITION.TOP_RIGHT,
        });

        // Reset edit mode and clear editable data
        onEditModeChange && onEditModeChange(false);
        onSaveData && onSaveData({}); // Clear editable data

        // Refresh the table data by triggering filter change with new object reference
        if (onFilterChange) {
          onFilterChange({ ...filter });
        }
      } else {
        toast.error("Failed to save data", {
          position: toast.POSITION.TOP_RIGHT,
        });
      }
    } catch (error) {
      setLoaderDisplay(false);
      toast.error("Error saving data", {
        position: toast.POSITION.TOP_RIGHT,
      });
      console.error('Save error:', error);
    }
  };

  useEffect(() => {
    if (!glbRegulationId) return;
    getScopeCategories(glbRegulationId);
    getEnterprisesByLevel(1);
    getEnterprisesByLevel(2);
  }, [glbRegulationId]);

  useEffect(() => {
    let years = [];
    for (let i = currentYear; i >= startYear; i--) {
      years.push(
        <option value={i} key={i}>{i}</option>
      );
    }
    setYearOptions(years);
  }, []);

  useEffect(() => {
    onFilterChange(filter);
  }, [filter]);

  // Initialize date validation on mount
  useEffect(() => {
    validateDateRange(filter.periodFrom, filter.periodTo);
  }, []);

  // Initialize from/to period state variables from filter
  useEffect(() => {
    const fromDateParts = filter.periodFrom.split('-');
    const toDateParts = filter.periodTo.split('-');

    if (fromDateParts.length === 3) {
      setFromPeriodYear(parseInt(fromDateParts[0]));
      setFromPeriodMonth(fromDateParts[1]);
    }

    if (toDateParts.length === 3) {
      setToPeriodYear(parseInt(toDateParts[0]));
      setToPeriodMonth(toDateParts[1]);
    }
  }, [filter.periodFrom, filter.periodTo]);

  // Validate from/to period range when state changes
  useEffect(() => {
    if (fromPeriodYear && fromPeriodMonth && toPeriodYear && toPeriodMonth) {
      validateFromToPeriodRange();
    }
  }, [fromPeriodYear, fromPeriodMonth, toPeriodYear, toPeriodMonth]);

  // Effect to handle view changes and update business unit IDs
  useEffect(() => {
    if (activeView === 'combined' && parentEntities.length > 0) {
      const allEntityIds = getAllEntityIds();
      setFilter(prevFilter => ({
        ...prevFilter,
        businessUnitIds: allEntityIds
      }));
    } else if (activeView === 'entity') {
      // Clear entity selections when switching to entity view
      setFilter(prevFilter => ({
        ...prevFilter,
        businessUnitIds: []
      }));
    } else if (activeView === 'workbench') {
      // Select all parent and child entities in workbench view
      const allEntityIds = getAllEntityIds();
      setFilter(prevFilter => ({
        ...prevFilter,
        businessUnitIds: allEntityIds
      }));
    }

    // Notify parent component about view change to clear table data
    if (onViewChange) {
      onViewChange(activeView);
    }
  }, [activeView, parentEntities, childEntities]);

  // Effect to reset date when switching from workbench to other views
  useEffect(() => {
    // Only reset if we're switching from workbench to another view
    if (activeView !== 'workbench') {
      const currentDate = new Date();
      const currentYear = currentDate.getFullYear();
      const currentMonth = String(currentDate.getMonth() + 1).padStart(2, '0');
      const currentPeriod = `${currentYear}-${currentMonth}-01`;
      const currentPeriodFrom = `${currentYear}-${currentMonth}-01`;
      const currentPeriodTo = getLastDayOfMonth(currentYear, currentMonth);

      // Update the filter with current month/year
      setFilter(prevFilter => ({
        ...prevFilter,
        period: currentPeriod,
        periodFrom: currentPeriodFrom,
        periodTo: currentPeriodTo
      }));

      // Update the state variables
      setAnnualYear(currentYear);
      setFrequencyMonth(currentMonth);

      // Update from/to period state variables
      setFromPeriodYear(currentYear);
      setFromPeriodMonth(currentMonth);
      setToPeriodYear(currentYear);
      setToPeriodMonth(currentMonth);
    }
  }, [activeView]);

  const getScopeCategories = (id) => {
    GHGEmissionService.getMatScopeCategory(id)
      .then((response) => {
        if (response.status === 200) {
          setScopeCategoryData(response.data.data ?? []);
        }
      })
      .catch((error) => { });
  }

  const getCategories = () => {
    if (!filter.scope || filter.scope.length === 0) return [];

    // Get categories from all selected scopes
    const allCategories = new Set();
    filter.scope.forEach(scope => {
      if (scopeCategoryData[scope]) {
        scopeCategoryData[scope].forEach(category => {
          allCategories.add(category);
        });
      }
    });

    return Array.from(allCategories).sort();
  };

  const getEnterprisesByLevel = (level) => {
    CompanyService.getEnterprisesByLevel(level).then(res => {
      if (res.status !== 200) return;

      const entities = res.data.sort((a, b) => a.sortOrder - b.sortOrder);
      if (level === 1) {
        setParentEntities(entities);
      } else if (level === 2) {
        setChildEntities(entities);
      }
    }).catch(err => { });
  };

  const handleParentEntityChange = (entityId, checked) => {
    let updatedBusinessUnitIds = [];

    if (checked) {
      // For entity view, only allow one entity at a time
      if (activeView === 'entity') {
        updatedBusinessUnitIds = [entityId];
        setShowParentEntityDropdown(false);
      } else {
        // For other views, allow multiple selection
        updatedBusinessUnitIds = [...filter.businessUnitIds];
        if (!updatedBusinessUnitIds.includes(entityId)) {
          updatedBusinessUnitIds.push(entityId);
        }
      }
    } else {
      updatedBusinessUnitIds = filter.businessUnitIds.filter(id => id !== entityId);
    }

    setFilter({ ...filter, businessUnitIds: updatedBusinessUnitIds });
  };

  const handleParentEntitySelectAll = () => {
    if (activeView === 'entity') {
      // For entity view, select the first parent entity
      if (parentEntities.length > 0) {
        setFilter({ ...filter, businessUnitIds: [parentEntities[0].id] });
      }
    } else {
      // For other views, select all parent entities
      const allParentIds = parentEntities.map(entity => entity.id);
      setFilter({ ...filter, businessUnitIds: allParentIds });
    }
  };

  const handleParentEntityUnselectAll = () => {
    // Remove all parent entities from selection
    const remainingIds = filter.businessUnitIds.filter(id =>
      !parentEntities.some(entity => entity.id === id)
    );
    setFilter({ ...filter, businessUnitIds: remainingIds });
  };

  const handleChildEntityChange = (entityId, checked) => {
    let updatedBusinessUnitIds = [];

    if (checked) {
      // For entity view, only allow one entity at a time
      if (activeView === 'entity') {
        updatedBusinessUnitIds = [entityId];
        setShowChildEntityDropdown(false);
      } else {
        // For other views, allow multiple selection
        updatedBusinessUnitIds = [...filter.businessUnitIds];
        if (!updatedBusinessUnitIds.includes(entityId)) {
          updatedBusinessUnitIds.push(entityId);
        }
      }
    } else {
      updatedBusinessUnitIds = filter.businessUnitIds.filter(id => id !== entityId);
    }

    setFilter({ ...filter, businessUnitIds: updatedBusinessUnitIds });
  };

  const handleChildEntitySelectAll = () => {
    if (activeView === 'entity') {
      // For entity view, select the first child entity
      if (childEntities.length > 0) {
        setFilter({ ...filter, businessUnitIds: [childEntities[0].id] });
      }
    } else {
      // For other views, select all child entities
      const allChildIds = childEntities.map(entity => entity.id);
      setFilter({ ...filter, businessUnitIds: allChildIds });
    }
  };

  const handleChildEntityUnselectAll = () => {
    // Remove all child entities from selection
    const remainingIds = filter.businessUnitIds.filter(id =>
      !childEntities.some(entity => entity.id === id)
    );
    setFilter({ ...filter, businessUnitIds: remainingIds });
  };

  const getSelectedParentEntitiesCount = () => {
    return parentEntities.filter(entity => filter.businessUnitIds.includes(Number(entity.id)) || filter.businessUnitIds.includes(String(entity.id))).length;
  };

  const getSelectedChildEntitiesCount = () => {
    return childEntities.filter(entity => filter.businessUnitIds.includes(Number(entity.id)) || filter.businessUnitIds.includes(String(entity.id))).length;
  };

  const areAllParentEntitiesSelected = () => {
    return parentEntities.length > 0 && getSelectedParentEntitiesCount() === parentEntities.length;
  };

  const areAllChildEntitiesSelected = () => {
    return childEntities.length > 0 && getSelectedChildEntitiesCount() === childEntities.length;
  };

  const areNoParentEntitiesSelected = () => {
    return getSelectedParentEntitiesCount() === 0;
  };

  const areNoChildEntitiesSelected = () => {
    return getSelectedChildEntitiesCount() === 0;
  };

  const isEntitySelected = (entityId) => {
    return filter.businessUnitIds.includes(Number(entityId)) || filter.businessUnitIds.includes(String(entityId));
  };

  const handleScopeChange = (scope, checked) => {
    let updatedScopes = [...filter.scope];

    if (checked) {
      if (!updatedScopes.includes(scope)) {
        updatedScopes.push(scope);
      }
    } else {
      // Prevent unselecting the last item
      if (updatedScopes.length > 1) {
        updatedScopes = updatedScopes.filter(s => s !== scope);
      } else {
        // If trying to unselect the last item, show a warning and don't allow it
        toast.warning(t('ScopeRequired.label'), {
          position: toast.POSITION.TOP_RIGHT,
        });
        return;
      }
    }

    // Get all categories from the updated scopes
    const allCategories = new Set();
    updatedScopes.forEach(scopeKey => {
      if (scopeCategoryData[scopeKey]) {
        scopeCategoryData[scopeKey].forEach(category => {
          allCategories.add(category);
        });
      }
    });

    setFilter({
      ...filter,
      scope: updatedScopes,
      category: Array.from(allCategories).sort() // Select all categories from the updated scopes
    });
    // Don't close dropdown for scope as it supports multiple selection
  };

  const handleScopeSelectAll = () => {
    const allScopes = Object.keys(scopeCategoryData)
      .sort((a, b) => parseInt(a.replace('Scope ', '')) - parseInt(b.replace('Scope ', '')));

    // Get all categories from all scopes
    const allCategories = new Set();
    allScopes.forEach(scopeKey => {
      if (scopeCategoryData[scopeKey]) {
        scopeCategoryData[scopeKey].forEach(category => {
          allCategories.add(category);
        });
      }
    });

    setFilter({
      ...filter,
      scope: allScopes,
      category: Array.from(allCategories).sort() // Select all categories
    });
  };



  const getSelectedScopesCount = () => {
    return filter.scope.length;
  };

  const getScopeDisplayText = () => {
    const allScopes = Object.keys(scopeCategoryData)
      .sort((a, b) => parseInt(a.replace('Scope ', '')) - parseInt(b.replace('Scope ', '')));
    const selectedCount = getSelectedScopesCount();

    // If no scopes available, return "All"
    if (allScopes.length === 0) {
      return "All";
    }

    // If no scopes selected or all scopes selected, return "All"
    if (selectedCount === 0 || selectedCount === allScopes.length) {
      return "All";
    }

    // Otherwise return the count
    return selectedCount.toString();
  };

  const areAllScopesSelected = () => {
    const allScopes = Object.keys(scopeCategoryData)
      .sort((a, b) => parseInt(a.replace('Scope ', '')) - parseInt(b.replace('Scope ', '')));
    return allScopes.length > 0 && getSelectedScopesCount() === allScopes.length;
  };



  const handleCategoryChange = (category, checked) => {
    let updatedCategories = [...filter.category];

    if (checked) {
      if (!updatedCategories.includes(category)) {
        updatedCategories.push(category);
      }
    } else {
      // Prevent unselecting the last item
      if (updatedCategories.length > 1) {
        updatedCategories = updatedCategories.filter(c => c !== category);
      } else {
        // If trying to unselect the last item, show a warning and don't allow it
        toast.warning(t('CategoryRequired.label'), {
          position: toast.POSITION.TOP_RIGHT,
        });
        return;
      }
    }

    setFilter({ ...filter, category: updatedCategories });
    // Don't close dropdown for category as it supports multiple selection
  };

  const handleCategorySelectAll = () => {
    const categories = getCategories();
    setFilter({ ...filter, category: categories });
  };



  const areAllCategoriesSelected = () => {
    const categories = getCategories();
    return categories.length > 0 && getSelectedCategoriesCount() === categories.length;
  };



  const getSelectedCategoriesCount = () => {
    return filter.category.length;
  };

  const getCategoryDisplayText = () => {
    const categories = getCategories();
    const selectedCount = getSelectedCategoriesCount();

    // If no categories available, return "All"
    if (categories.length === 0) {
      return "All";
    }

    // If no categories selected or all categories selected, return "All"
    if (selectedCount === 0 || selectedCount === categories.length) {
      return "All";
    }

    // Otherwise return the count
    return selectedCount.toString();
  };

  const isScopeSelected = (scope) => {
    return filter.scope.includes(scope);
  };

  const isCategorySelected = (category) => {
    return filter.category.includes(category);
  };

  const handleSearchToggle = () => {
    setIsSearchVisible(!isSearchVisible);
    if (isSearchVisible) {
      setSearchTerm('');
      onSearchChange && onSearchChange('');
    }
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    onSearchChange && onSearchChange(value);
  };

  const handleYearChange = (year) => {
    setAnnualYear(year);
    const newPeriod = `${year}-${frequencymonth}-01`;
    const periodFrom = `${year}-${frequencymonth}-01`;
    const periodTo = getLastDayOfMonth(year, frequencymonth);
    setFilter({ ...filter, period: newPeriod, periodFrom: periodFrom, periodTo: periodTo });
    setShowPeriodDropdown(false);
  };

  const handleMonthChange = (month) => {
    setFrequencyMonth(month);
    const newPeriod = `${annualyear}-${month}-01`;
    const periodFrom = `${annualyear}-${month}-01`;
    const periodTo = getLastDayOfMonth(annualyear, month);
    setFilter({ ...filter, period: newPeriod, periodFrom: periodFrom, periodTo: periodTo });
    setShowPeriodDropdown(false);
  };



  const getLastDayOfMonth = (year, month) => {
    const lastDay = new Date(year, month, 0).getDate();
    return `${year}-${month.padStart(2, '0')}-${lastDay.toString().padStart(2, '0')}`;
  };

  // Date validation functions
  const validateDateRange = (fromDate, toDate) => {
    const from = new Date(fromDate);
    const to = new Date(toDate);

    // Reset errors
    setPeriodFromError('');
    setPeriodToError('');

    let isValid = true;

    // Check if from date is valid
    if (isNaN(from.getTime())) {
      setPeriodFromError('Invalid date format');
      isValid = false;
    }

    // Check if to date is valid
    if (isNaN(to.getTime())) {
      setPeriodToError('Invalid date format');
      isValid = false;
    }

    // Check if from date is before or equal to to date
    if (from > to) {
      setPeriodFromError('From date must be before or equal to to date');
      isValid = false;
    }

    return isValid;
  };

  const handlePeriodFromChange = (date) => {
    const newFilter = { ...filter, periodFrom: date };

    // Validate the new date range
    const fromDate = new Date(date);
    const toDate = new Date(filter.periodTo);

    if (fromDate > toDate) {
      setPeriodFromError('From date must be before or equal to to date');
      setPeriodToError('To date must be after or equal to from date');
      return; // Don't update filter if validation fails
    }

    // Clear errors if validation passes
    setPeriodFromError('');
    setPeriodToError('');

    setFilter(newFilter);
    setShowPeriodFromDropdown(false);

    // Notify parent component
    if (onFilterChange) {
      onFilterChange(newFilter);
    }
  };

  const handlePeriodToChange = (date) => {
    const newFilter = { ...filter, periodTo: date };

    // Validate the new date range
    const fromDate = new Date(filter.periodFrom);
    const toDate = new Date(date);

    if (fromDate > toDate) {
      setPeriodFromError('From date must be before or equal to to date');
      setPeriodToError('To date must be after or equal to from date');
      return; // Don't update filter if validation fails
    }

    // Clear errors if validation passes
    setPeriodFromError('');
    setPeriodToError('');

    setFilter(newFilter);
    setShowPeriodToDropdown(false);

    // Notify parent component
    if (onFilterChange) {
      onFilterChange(newFilter);
    }
  };

  const formatDateForDisplay = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Initialize all scopes and categories as selected when data is loaded
  useEffect(() => {
    if (Object.keys(scopeCategoryData).length > 0) {
      const allScopes = Object.keys(scopeCategoryData)
        .sort((a, b) => parseInt(a.replace('Scope ', '')) - parseInt(b.replace('Scope ', '')));

      // Get all categories from all scopes
      const allCategories = new Set();
      allScopes.forEach(scope => {
        if (scopeCategoryData[scope]) {
          scopeCategoryData[scope].forEach(category => {
            allCategories.add(category);
          });
        }
      });

      setFilter(prevFilter => ({
        ...prevFilter,
        scope: allScopes,
        category: Array.from(allCategories).sort()
      }));
    }
  }, [scopeCategoryData]);

  return (
    <div className="ghg-emission-header" data-testid="ghg-emission-header">
      <div className='List_header mb-2'>
        <div>
          <Breadcrumb className="h5 m-0">
            <Breadcrumb.Item>{t("UAEFDLBreadcrumb.emissions.label")}</Breadcrumb.Item>
            <Breadcrumb.Item active>{t("DataManagement.label")}</Breadcrumb.Item>
          </Breadcrumb>
        </div>
        <div className='header-actions'>
          <ButtonGroup aria-label="Basic example">
            <Button
              variant={activeView == 'combined' ? 'primary' : 'secondary'}
              onClick={() => {
                setActiveView('combined');
                onViewChange && onViewChange('combined');
              }}
            >
              Combined
            </Button>
            <Button
              variant={activeView == 'entity' ? 'primary' : 'secondary'}
              onClick={() => {
                setActiveView('entity');
                onViewChange && onViewChange('entity');
              }}
            >
              Entity
            </Button>
            {/* <Button
              variant={activeView == 'workbench' ? 'primary' : 'secondary'}
              onClick={() => {
                setActiveView('workbench');
                onViewChange && onViewChange('workbench');
              }}
            >
              Workbench
            </Button> */}
          </ButtonGroup>
        </div>
      </div>

      <div className="ghg-header-filters-row">
        {/* Period dropdown - different for workbench view and edit mode */}
        {activeView === 'workbench' ? (
          <>
            <div className="ghg-header-pill">
              <Dropdown show={showFromPeriodDropdown} onToggle={(isOpen) => setShowFromPeriodDropdown(isOpen)}>
                <Dropdown.Toggle
                  id="dropdown-from-period"
                  className={fromPeriodError ? 'ghg-dropdown-toggle-error' : 'ghg-dropdown-toggle'}
                >
                  From: {getFormattedFromPeriod()}
                </Dropdown.Toggle>
                <Dropdown.Menu className="Edit_dropdown p-2">
                  <div className="row m-0">
                    <div className="col-md-5 p-2">
                      <label className="mb-1">{t("Year.label")}</label>
                      <select className="form-select"
                        onChange={(e) => handleFromPeriodYearChange(e.target.value)}
                        value={fromPeriodYear}>
                        {yearOptions}
                      </select>
                    </div>

                    <div className="col-md-7 p-2">
                      <label className="mb-1">{t("Month.label")}</label>
                      <select
                        className="form-select"
                        value={fromPeriodMonth}
                        onChange={(e) => handleFromPeriodMonthChange(e.target.value)}>
                        {
                          optionsMonth?.map((items) => (
                            <option value={items.value} key={items.value}>
                              {items.label}
                            </option>
                          ))
                        }
                      </select>
                    </div>
                  </div>
                  {fromPeriodError && (
                    <div className="text-danger mt-2 p-2 ghg-error-message">
                      {fromPeriodError}
                    </div>
                  )}
                </Dropdown.Menu>
              </Dropdown>
            </div>
            <div className="ghg-header-divider">|</div>
            <div className="ghg-header-pill">
              <Dropdown show={showToPeriodDropdown} onToggle={(isOpen) => setShowToPeriodDropdown(isOpen)}>
                <Dropdown.Toggle
                  id="dropdown-to-period"
                  className={toPeriodError ? 'ghg-dropdown-toggle-error' : 'ghg-dropdown-toggle'}
                >
                  To: {getFormattedToPeriod()}
                </Dropdown.Toggle>
                <Dropdown.Menu className="Edit_dropdown p-2">
                  <div className="row m-0">
                    <div className="col-md-5 p-2">
                      <label className="mb-1">{t("Year.label")}</label>
                      <select className="form-select"
                        onChange={(e) => handleToPeriodYearChange(e.target.value)}
                        value={toPeriodYear}>
                        {yearOptions}
                      </select>
                    </div>

                    <div className="col-md-7 p-2">
                      <label className="mb-1">{t("Month.label")}</label>
                      <select
                        className="form-select"
                        value={toPeriodMonth}
                        onChange={(e) => handleToPeriodMonthChange(e.target.value)}>
                        {
                          optionsMonth?.map((items) => (
                            <option value={items.value} key={items.value}>
                              {items.label}
                            </option>
                          ))
                        }
                      </select>
                    </div>
                  </div>
                  {toPeriodError && (
                    <div className="text-danger mt-2 p-2 ghg-error-message">
                      {toPeriodError}
                    </div>
                  )}
                </Dropdown.Menu>
              </Dropdown>
            </div>
          </>
        ) : isEditMode ? (
          // Show only current period dropdown when in edit mode
          <div className="ghg-header-pill">
            <Dropdown show={showPeriodDropdown} onToggle={(isOpen) => setShowPeriodDropdown(isOpen)}>
              <Dropdown.Toggle
                id="dropdown-basic"
                className="ghg-dropdown-toggle-edit"
              >
                Period: {getFormattedPeriod()}
              </Dropdown.Toggle>
              <Dropdown.Menu className="Edit_dropdown p-2">
                <div className="row m-0">
                  <div className="col-md-5 p-2">
                    <label className="mb-1">{t("Year.label")}</label>
                    <select className="form-select "
                      onChange={(e) => {
                        handleYearChange(e.target.value);
                      }}
                      value={annualyear}>
                      {yearOptions}
                    </select>
                  </div>

                  <div className="col-md-7 p-2">
                    <label className="mb-1">{t("Month.label")}</label>
                    <select
                      className="form-select "
                      value={frequencymonth}
                      onChange={(e) => {
                        handleMonthChange(e.target.value);
                      }}>
                      {
                        optionsMonth?.map((items) => (
                          <option value={items.value} key={items.value}>
                            {items.label}
                          </option>
                        ))
                      }
                    </select>
                  </div>
                </div>
              </Dropdown.Menu>
            </Dropdown>
          </div>
        ) : (
          // Show from/to period dropdowns for combined and entity views
          <>
            <div className="ghg-header-pill">
              <Dropdown show={showFromPeriodDropdown} onToggle={(isOpen) => setShowFromPeriodDropdown(isOpen)}>
                <Dropdown.Toggle
                  id="dropdown-from-period"
                  className={fromPeriodError ? 'ghg-dropdown-toggle-error' : 'ghg-dropdown-toggle'}
                >
                  From: {getFormattedFromPeriod()}
                </Dropdown.Toggle>
                <Dropdown.Menu className="Edit_dropdown p-2">
                  <div className="row m-0">
                    <div className="col-md-5 p-2">
                      <label className="mb-1">{t("Year.label")}</label>
                      <select className="form-select"
                        onChange={(e) => handleFromPeriodYearChange(e.target.value)}
                        value={fromPeriodYear}>
                        {yearOptions}
                      </select>
                    </div>

                    <div className="col-md-7 p-2">
                      <label className="mb-1">{t("Month.label")}</label>
                      <select
                        className="form-select"
                        value={fromPeriodMonth}
                        onChange={(e) => handleFromPeriodMonthChange(e.target.value)}>
                        {
                          optionsMonth?.map((items) => (
                            <option value={items.value} key={items.value}>
                              {items.label}
                            </option>
                          ))
                        }
                      </select>
                    </div>
                  </div>
                  {fromPeriodError && (
                    <div className="text-danger mt-2 p-2 ghg-error-message">
                      {fromPeriodError}
                    </div>
                  )}
                </Dropdown.Menu>
              </Dropdown>
            </div>
            <div className="ghg-header-divider">|</div>
            <div className="ghg-header-pill">
              <Dropdown show={showToPeriodDropdown} onToggle={(isOpen) => setShowToPeriodDropdown(isOpen)}>
                <Dropdown.Toggle
                  id="dropdown-to-period"
                  className={toPeriodError ? 'ghg-dropdown-toggle-error' : 'ghg-dropdown-toggle'}
                >
                  To: {getFormattedToPeriod()}
                </Dropdown.Toggle>
                <Dropdown.Menu className="Edit_dropdown p-2">
                  <div className="row m-0">
                    <div className="col-md-5 p-2">
                      <label className="mb-1">{t("Year.label")}</label>
                      <select className="form-select"
                        onChange={(e) => handleToPeriodYearChange(e.target.value)}
                        value={toPeriodYear}>
                        {yearOptions}
                      </select>
                    </div>

                    <div className="col-md-7 p-2">
                      <label className="mb-1">{t("Month.label")}</label>
                      <select
                        className="form-select"
                        value={toPeriodMonth}
                        onChange={(e) => handleToPeriodMonthChange(e.target.value)}>
                        {
                          optionsMonth?.map((items) => (
                            <option value={items.value} key={items.value}>
                              {items.label}
                            </option>
                          ))
                        }
                      </select>
                    </div>
                  </div>
                  {toPeriodError && (
                    <div className="text-danger mt-2 p-2 ghg-error-message">
                      {toPeriodError}
                    </div>
                  )}
                </Dropdown.Menu>
              </Dropdown>
            </div>
          </>
        )}
        <div className="ghg-header-divider">|</div>
        <div className="ghg-header-pill">
          <Dropdown show={showScopeDropdown} onToggle={(isOpen) => setShowScopeDropdown(isOpen)}>
            <Dropdown.Toggle
              id="dropdown-basic"
              className="ghg-dropdown-toggle"
            >
              Scope: {getScopeDisplayText()}
            </Dropdown.Toggle>
            <Dropdown.Menu className="p-2 ghg-dropdown-menu">
              {/* Select All Button */}
              <div className="d-flex justify-content-center align-items-center mb-2 pb-2 border-bottom">
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleScopeSelectAll}
                  disabled={areAllScopesSelected()}
                  className="ghg-select-all-btn"
                >
                  {t("SelectAll.label")}
                </Button>
              </div>

              {/* Scope Options */}
              {Object.keys(scopeCategoryData)
                .sort((a, b) => parseInt(a.replace('Scope ', '')) - parseInt(b.replace('Scope ', '')))
                .map((scope) => (
                  <div key={scope} className="d-flex align-items-center">
                    <Form.Check
                      type="checkbox"
                      id={`scope-${scope}`}
                      checked={isScopeSelected(scope)}
                      onChange={(e) => handleScopeChange(scope, e.target.checked)}
                      className="me-2"
                    />
                    <label htmlFor={`scope-${scope}`} className="mb-0 cursor-pointer">
                      {scope}
                    </label>
                  </div>
                ))}
            </Dropdown.Menu>
          </Dropdown>
        </div>
        <div className="ghg-header-divider">|</div>
        <div className="ghg-header-pill">
          <Dropdown show={showCategoryDropdown} onToggle={(isOpen) => setShowCategoryDropdown(isOpen)}>
            <Dropdown.Toggle
              id="dropdown-basic"
              className="ghg-dropdown-toggle"
            >
              Category: {getCategoryDisplayText()}
            </Dropdown.Toggle>
            <Dropdown.Menu className="p-2 ghg-dropdown-menu ghg-dropdown-menu-wide">
              {/* Select All Button */}
              <div className="d-flex justify-content-center align-items-center mb-2 pb-2 border-bottom">
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleCategorySelectAll}
                  disabled={areAllCategoriesSelected()}
                  className="ghg-select-all-btn"
                >
                  {t("SelectAll.label")}
                </Button>
              </div>

              {/* Category Options with fixed height and scroll */}
              <div className="ghg-dropdown-scrollable">
                {getCategories().map((category) => (
                  <div key={category} className="d-flex align-items-center mb-1">
                    <Form.Check
                      type="checkbox"
                      id={`category-${category}`}
                      checked={isCategorySelected(category)}
                      onChange={(e) => handleCategoryChange(category, e.target.checked)}
                      className="me-2"
                    />
                    <label htmlFor={`category-${category}`} className="mb-0 cursor-pointer">
                      {category}
                    </label>
                  </div>
                ))}
              </div>
            </Dropdown.Menu>
          </Dropdown>
        </div>
        <div className="ghg-header-divider">|</div>
        <div className="ghg-header-pill">
          <Dropdown show={showParentEntityDropdown} onToggle={(isOpen) => setShowParentEntityDropdown(isOpen)}>
            <Dropdown.Toggle
              id="dropdown-basic"
              className="ghg-dropdown-toggle"
            >
              Parent Entity {getSelectedParentEntitiesCount() > 0 && `(${getSelectedParentEntitiesCount()})`}
            </Dropdown.Toggle>
            <Dropdown.Menu className="p-2 ghg-dropdown-menu ghg-dropdown-menu-wide">
              {/* Select All / Unselect All buttons - Only show in combined view */}
              {activeView !== 'entity' && (
                <div className="d-flex justify-content-between mb-2 pb-2 border-bottom">
                  <button
                    type="button"
                    className="btn btn-sm btn-primary ghg-select-all-btn"
                    onClick={handleParentEntitySelectAll}
                    disabled={areAllParentEntitiesSelected()}
                  >
                    Select All
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary ghg-unselect-all-btn"
                    onClick={handleParentEntityUnselectAll}
                    disabled={areNoParentEntitiesSelected()}
                  >
                    Unselect All
                  </button>
                </div>
              )}
              {/* Entity list with fixed height and scroll */}
              <div className="ghg-dropdown-scrollable">
                {parentEntities.map((pEnt) => (
                  <div key={pEnt.id} className="d-flex align-items-center mb-1">
                    <Form.Check
                      type="checkbox"
                      id={`parent-${pEnt.id}`}
                      checked={isEntitySelected(pEnt.id)}
                      onChange={(e) => handleParentEntityChange(pEnt.id, e.target.checked)}
                      className="me-2"
                    />
                    <label htmlFor={`parent-${pEnt.id}`} className="mb-0 cursor-pointer">
                      {pEnt?.name}
                    </label>
                  </div>
                ))}
              </div>
            </Dropdown.Menu>
          </Dropdown>
        </div>
        <div className="ghg-header-divider">|</div>
        <div className="ghg-header-pill">
          <Dropdown show={showChildEntityDropdown} onToggle={(isOpen) => setShowChildEntityDropdown(isOpen)}>
            <Dropdown.Toggle
              id="dropdown-basic"
              className="ghg-dropdown-toggle"
            >
              Child Entity {getSelectedChildEntitiesCount() > 0 && `(${getSelectedChildEntitiesCount()})`}
            </Dropdown.Toggle>
            <Dropdown.Menu className="p-2 ghg-dropdown-menu ghg-dropdown-menu-wide">
              {/* Select All / Unselect All buttons - Only show in combined view */}
              {activeView !== 'entity' && (
                <div className="d-flex justify-content-between mb-2 pb-2 border-bottom">
                  <button
                    type="button"
                    className="btn btn-sm btn-primary ghg-select-all-btn"
                    onClick={handleChildEntitySelectAll}
                    disabled={areAllChildEntitiesSelected()}
                  >
                    Select All
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary ghg-unselect-all-btn"
                    onClick={handleChildEntityUnselectAll}
                    disabled={areNoChildEntitiesSelected()}
                  >
                    Unselect All
                  </button>
                </div>
              )}
              {/* Entity list with fixed height and scroll */}
              <div className="ghg-dropdown-scrollable">
                {childEntities.map((cEnt) => (
                  <div key={cEnt.id} className="d-flex align-items-center mb-1">
                    <Form.Check
                      type="checkbox"
                      id={`child-${cEnt.id}`}
                      checked={isEntitySelected(cEnt.id)}
                      onChange={(e) => handleChildEntityChange(cEnt.id, e.target.checked)}
                      className="me-2"
                    />
                    <label htmlFor={`child-${cEnt.id}`} className="mb-0 cursor-pointer">
                      {cEnt?.name}
                    </label>
                  </div>
                ))}
              </div>
            </Dropdown.Menu>
          </Dropdown>
        </div>
        <div className="ghg-header-divider">|</div>

        <div className="ghg-header-icons">
          <div className="ghg-search-container">
            {isSearchVisible && (
              <input
                type="text"
                placeholder="Search emission sources..."
                value={searchTerm}
                onChange={handleSearchChange}
                className="ghg-search-input"
                autoFocus
              />
            )}

            <OverlayTrigger
              placement="bottom"
              overlay={<Tooltip id="button-tooltip-2">Search</Tooltip>}
            >
              <span
                className="ghg-header-icon ghg-icon-btn"
                title="Search"
                onClick={handleSearchToggle}
              >
                <MdSearch />
              </span>
            </OverlayTrigger>
          </div>
          {/* Hide edit button in workbench view */}
          {activeView !== 'workbench' && (
            !isEditMode ? (
              <OverlayTrigger
                placement="bottom"
                overlay={<Tooltip id="button-tooltip-2">Edit</Tooltip>}
              >
                <span
                  className="ghg-header-icon ghg-icon-btn"
                  onClick={handleEditModeEnter}
                  hidden={!hasGHGEmissionsUpdatePermission()}
                >
                  <MdEdit />
                </span>
              </OverlayTrigger>
            ) : (
              <>
                <OverlayTrigger
                  placement="bottom"
                  overlay={<Tooltip id="button-tooltip-2">Save</Tooltip>}
                >
                  <span
                    className={`ghg-header-icon ghg-icon-btn ghg-icon-btn-save ${
                      hasValidationErrors 
                        ? 'ghg-icon-btn-error' 
                        : (hasChanges ? 'ghg-icon-btn-active' : 'ghg-icon-btn-inactive')
                    } ${(hasChanges && !hasValidationErrors) ? '' : 'ghg-icon-btn-disabled'}`}
                    title={hasValidationErrors ? "Fix validation errors before saving" : "Save"}
                    onClick={handleSaveData}
                  >
                    <MdSave />
                  </span>
                </OverlayTrigger>
                <OverlayTrigger
                  placement="bottom"
                  overlay={<Tooltip id="button-tooltip-2">Cancel</Tooltip>}
                >
                  <span
                    className="ghg-header-icon cancel-icon ghg-icon-btn"
                    title="Cancel"
                    onClick={handleCancelEdit}
                  >
                    <MdCancel />
                  </span>
                </OverlayTrigger>
              </>
            )
          )}
          <OverlayTrigger
            placement="bottom"
            overlay={<Tooltip id="button-tooltip-2">Refresh</Tooltip>}
          >
            <span
              className="ghg-header-icon ghg-icon-btn"
              title="Refresh"
              onClick={handleRefresh}
            >
              <MdRefresh />
            </span>
          </OverlayTrigger>
          {/* Hide advanced filter button in edit mode */}
          {!isEditMode && (
            <OverlayTrigger
              placement="bottom"
              overlay={<Tooltip id="button-tooltip-2">{t("AdvancedPeriodModal.advancedPeriodFilter.label")}</Tooltip>}
            >
              <span
                className="ghg-header-icon ghg-icon-btn"
                onClick={handleAdvancedPeriodModalOpen}
              >
                <MdFilterList />
              </span>
            </OverlayTrigger>
          )}
          <OverlayTrigger
            placement="bottom"
            overlay={<Tooltip id="button-tooltip-download">{t("GHGEmissionDataManagement.downloadExcel.label") || "Download Excel"}</Tooltip>}
          >
            <span
              className="ghg-header-icon ghg-icon-btn"
              onClick={handleDownloadExcel}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleDownloadExcel(); } }}
              aria-label={t("GHGEmissionDataManagement.downloadExcel.label") || "Download Excel"}
            >
              <MdDownload />
            </span>
          </OverlayTrigger>
        </div>
        {/* <div className="ghg-header-divider">|</div>
        <div className="ghg-header-icons">
          <span className="ghg-header-icon" title="Search"><MdSearch /></span>
          <span className="ghg-header-icon" title="Edit"><MdEdit /></span>
          <span className="ghg-header-icon" title="Filter"><MdFilterList /></span>
          <span className="ghg-header-icon" title="Download"><MdDownload /></span>
        </div> */}
      </div>

      {/* Cancel Confirmation Modal */}
      <Modal show={showCancelConfirmation} onHide={handleCancelCancel} centered>
        <Modal.Header closeButton>
          <Modal.Title>Unsaved Changes</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          You have unsaved changes. Do you want to save your changes before exiting edit mode?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCancelConfirm}>
            Discard Changes
          </Button>
          <Button variant="primary" onClick={handleSaveData} disabled={hasValidationErrors}>
            Save Changes
          </Button>
          <Button variant="outline-secondary" onClick={handleCancelCancel}>
            Continue Editing
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Advanced Period Selection Modal */}
      <Modal show={showAdvancedPeriodModal} onHide={handleAdvancedPeriodModalClose} centered>
        <Modal.Header closeButton className="ghg-modal-header">
          <Modal.Title className="ghg-modal-title">
            {t("AdvancedPeriodModal.title.label")}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="ghg-modal-body">
          <Form>
            <div className='row m-0'>
              <div className='col-12 p-2'>
                <Form.Group className="mb-3">
                  <Form.Label className="ghg-form-label">
                    {t("AdvancedPeriodModal.periodType.label")}
                  </Form.Label>
                  <ButtonGroup aria-label="Period type selection" className="ghg-button-group-full">
                    <Button
                      variant={periodType === 'yearly' ? 'primary' : 'outline-primary'}
                      onClick={() => handlePeriodTypeChange('yearly')}
                      className={periodType === 'yearly' ? 'ghg-period-type-btn ghg-period-type-btn-active' : 'ghg-period-type-btn ghg-period-type-btn-inactive'}
                    >
                      {t("AdvancedPeriodModal.yearly.label")}
                    </Button>
                    <Button
                      variant={periodType === 'quarterly' ? 'primary' : 'outline-primary'}
                      onClick={() => handlePeriodTypeChange('quarterly')}
                      className={periodType === 'quarterly' ? 'ghg-period-type-btn ghg-period-type-btn-active' : 'ghg-period-type-btn ghg-period-type-btn-inactive'}
                    >
                      {t("AdvancedPeriodModal.quarterly.label")}
                    </Button>
                    <Button
                      variant={periodType === 'monthly' ? 'primary' : 'outline-primary'}
                      onClick={() => handlePeriodTypeChange('monthly')}
                      className={periodType === 'monthly' ? 'ghg-period-type-btn ghg-period-type-btn-active' : 'ghg-period-type-btn ghg-period-type-btn-inactive'}
                    >
                      {t("AdvancedPeriodModal.monthly.label")}
                    </Button>
                    <Button
                      variant={periodType === 'custom' ? 'primary' : 'outline-primary'}
                      onClick={() => handlePeriodTypeChange('custom')}
                      className={periodType === 'custom' ? 'ghg-period-type-btn ghg-period-type-btn-active' : 'ghg-period-type-btn ghg-period-type-btn-inactive'}
                    >
                      {t("AdvancedPeriodModal.custom.label")}
                    </Button>
                  </ButtonGroup>
                </Form.Group>
              </div>


              {periodType === 'yearly' && (
                <div className='col-12 p-2'>
                  <Form.Group className="mb-0">
                    <Form.Label className="ghg-form-label-small">
                      {t("AdvancedPeriodModal.year.label")}
                    </Form.Label>
                    <select
                      className="form-select ghg-modal-form-select"
                      value={selectedYear}
                      onChange={(e) => handleAdvancedYearChange(e.target.value)}
                    >
                      {generateYearOptions()}
                    </select>
                  </Form.Group>
                </div>
              )}

              {periodType === 'quarterly' && (
                <>

                  <div className='col-6 p-2'>
                    <Form.Group className="mb-0">
                      <Form.Label className="ghg-form-label-small">
                        {t("AdvancedPeriodModal.year.label")}
                      </Form.Label>
                      <select
                        className="form-select ghg-modal-form-select"
                        value={selectedYear}
                        onChange={(e) => handleAdvancedYearChange(e.target.value)}
                      >
                        {generateYearOptions()}
                      </select>
                    </Form.Group>
                  </div>
                  <div className='col-6 p-2'>
                    <Form.Group className="mb-0">
                      <Form.Label className="ghg-form-label-small">
                        {t("AdvancedPeriodModal.quarter.label")}
                      </Form.Label>
                      <select
                        className="form-select ghg-modal-form-select"
                        value={selectedQuarter}
                        onChange={(e) => handleQuarterChange(e.target.value)}
                      >
                        {generateQuarterOptions()}
                      </select>
                    </Form.Group>
                  </div>
                </>
              )}

              {periodType === 'monthly' && (
                <>
                  <div className='col-6 p-2'>
                    <Form.Group className="mb-0">
                      <Form.Label className="ghg-form-label-small">
                        {t("AdvancedPeriodModal.year.label")}
                      </Form.Label>
                      <select
                        className="form-select ghg-modal-form-select"
                        value={selectedYear}
                        onChange={(e) => handleAdvancedYearChange(e.target.value)}
                      >
                        {generateYearOptions()}
                      </select>
                    </Form.Group>
                  </div>
                  <div className='col-6 p-2'>
                    <Form.Group className="mb-0">
                      <Form.Label className="ghg-form-label-small">
                        {t("AdvancedPeriodModal.month.label")}
                      </Form.Label>
                      <select
                        className="form-select ghg-modal-form-select"
                        value={selectedMonth}
                        onChange={(e) => handleAdvancedMonthChange(e.target.value)}
                      >
                        {generateMonthOptions()}
                      </select>
                    </Form.Group>
                  </div>
                </>
              )}

              {periodType === 'custom' && (
                <>
                  <div className='col-6 p-2'>
                    <Form.Group className="mb-0">
                      <Form.Label className="ghg-form-label-small">
                        {t("AdvancedPeriodModal.fromYear.label")}
                      </Form.Label>
                      <select
                        className={`form-select ghg-modal-form-select ${customPeriodError ? 'ghg-modal-form-select-error' : ''}`}
                        value={customFromYear}
                        onChange={(e) => handleCustomFromYearChange(e.target.value)}
                      >
                        {generateYearOptions()}
                      </select>
                    </Form.Group>
                  </div>
                  <div className='col-6 p-2'>
                    <Form.Group className="mb-0">
                      
                      <Form.Label className="ghg-form-label-small">
                        {t("AdvancedPeriodModal.fromMonth.label")}
                      </Form.Label>
                      <select
                        className="form-select"
                        value={customFromMonth}
                        onChange={(e) => handleCustomFromMonthChange(e.target.value)}
                        style={{
                          padding: '12px 16px',
                          fontSize: '14px',
                          border: customPeriodError ? '1px solid #dc3545' : '1px solid #d1d5db',
                          borderRadius: '6px',
                          backgroundColor: '#ffffff',
                          color: '#374151',
                          width: '100%'
                        }}
                      >
                        {generateMonthOptions()}
                      </select>
                    </Form.Group>
                  </div>
                  <div className='col-6 p-2'>
                    <Form.Group className="mb-0">
                      <Form.Label className="ghg-form-label-small">
                        {t("AdvancedPeriodModal.toYear.label")}
                      </Form.Label>
                      <select
                        className="form-select"
                        value={customToYear}
                        onChange={(e) => handleCustomToYearChange(e.target.value)}
                        style={{
                          padding: '12px 16px',
                          fontSize: '14px',
                          border: customPeriodError ? '1px solid #dc3545' : '1px solid #d1d5db',
                          borderRadius: '6px',
                          backgroundColor: '#ffffff',
                          color: '#374151',
                          width: '100%',
                        }}
                      >
                        {generateYearOptions()}
                      </select>
                    </Form.Group>
                  </div>

                  <div className='col-6 p-2'>
                    <Form.Group className="mb-0">
                     
                      <Form.Label className="ghg-form-label-small">
                        {t("AdvancedPeriodModal.toMonth.label")}
                      </Form.Label>
                      <select
                        className="form-select"
                        value={customToMonth}
                        onChange={(e) => handleCustomToMonthChange(e.target.value)}
                        style={{
                          padding: '12px 16px',
                          fontSize: '14px',
                          border: customPeriodError ? '1px solid #dc3545' : '1px solid #d1d5db',
                          borderRadius: '6px',
                          backgroundColor: '#ffffff',
                          color: '#374151',
                          width: '100%'
                        }}
                      >
                        {generateMonthOptions()}
                      </select>
                    </Form.Group>
                  </div>


                  {customPeriodError && (
                    <div className="ghg-custom-period-error">
                      {customPeriodError}
                    </div>
                  )}
                </>
              )}
            </div>
          </Form>
        </Modal.Body>
        <Modal.Footer className="ghg-modal-footer">
          <Button
            variant="secondary"
            onClick={handleAdvancedPeriodModalClose}
            className="ghg-modal-footer-btn ghg-modal-footer-btn-secondary"
          >
            {t("AdvancedPeriodModal.cancel.label")}
          </Button>
          <Button
            variant="primary"
            onClick={handleApplyAdvancedPeriod}
            disabled={periodType === 'custom' && customPeriodError !== ''}
            className="ghg-modal-footer-btn ghg-modal-footer-btn-primary"
          >
            {t("AdvancedPeriodModal.apply.label")}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default withTranslation()(UAEFDLDataManagementHeader); 